import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Policy } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeletePolicy } from '../hooks/use-policy-mutations';
import { policyPath } from '../schemas/policy-rules';

export interface DeletePolicyDialogProps {
  storeId: string;
  policy: Policy | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

/** Deleting leaves no redirect behind, so the confirmation says what stops working. */
export function DeletePolicyDialog({
  storeId,
  policy,
  onOpenChange,
  onDeleted,
}: DeletePolicyDialogProps) {
  const { t } = useTranslation(['policies', 'common', 'errors']);
  const remove = useDeletePolicy(storeId);
  const message = useErrorMessage();
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={policy !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('policies:delete.title', { title: policy?.title ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('policies:delete.description', { path: policyPath(policy?.handle ?? '') })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (policy) {
                remove.mutate(policy.id, {
                  onSuccess: () => {
                    onOpenChange(false);
                    onDeleted?.();
                  },
                });
              }
            }}
          >
            {remove.isPending ? t('policies:delete.submitting') : t('policies:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
